import { useSearch } from "@tanstack/react-router";
import { Button, Flex, Modal } from "antd";
import { useCCEntity } from "components/CC/context/CCEntityContext.tsx";
import { ConditionForm, ConditionFormData } from "components/CC/Edges/ConditionEdge/Forms/ConditionForm.tsx";
import { useSelectedEdge } from "components/CC/hooks/useDiagramActions";
import { DrawerId, FormId } from "components/CC/types/models.ts";
import { getCCDrawerSelectionParam } from "components/CC/utils/drawerUrlState.ts";
import { serializeForDirtyCheck } from "components/CC/utils/nodeDefaults";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useRef } from "react";
import { usePendingDrawerVisibility } from "utils/drawerVisibilityStore";

export const ConditionDrawer = () => {
    const drawerUtils = useDrawer();
    const { selectedEdge, deleteSelectedEdge, updateEdgeData } = useSelectedEdge();
    const selectionParam = getCCDrawerSelectionParam(DrawerId.Condition);

    // Close drawer (including from URL) if it was restored from URL but no edge is selected.
    // hadSelectionWhileOpenRef guards against closing when selectedEdge becomes briefly falsy
    // after a real interaction — only close when the drawer opened with no selection at all.
    const pendingVisibility = usePendingDrawerVisibility(DrawerId.Condition.toString());
    const isOpen = pendingVisibility ?? drawerUtils.isDrawerActive(DrawerId.Condition);
    const searchParams = useSearch({ strict: false }) as Record<string, unknown>;
    const hasUrlRestorationPending =
        typeof searchParams[selectionParam] === "string" && (searchParams[selectionParam] as string).length > 0;
    const { formFields, customFields } = useCCEntity();
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tC } = usePaldockTranslation("common");

    // Store latest form data so we can save on close
    const formDataRef = useRef<ConditionFormData | null>(null);
    // Store initial form data to compare against (set on first onChange call)
    const initialFormDataRef = useRef<string | null>(null);
    // Track if changes were saved via Done button
    const changesSavedRef = useRef(false);
    // Optional validator registered by form — returns true if valid (shows errors itself)
    const validateFormRef = useRef<(() => Promise<boolean>) | null>(null);

    const formData = selectedEdge?.data as Partial<ConditionFormData> | undefined;

    // Reset refs for next drawer open
    const resetRefs = useCallback(() => {
        formDataRef.current = null;
        initialFormDataRef.current = null;
        changesSavedRef.current = false;
        validateFormRef.current = null;
    }, []);

    const hadSelectionWhileOpenRef = useRef(false);
    const lastSelectedEdgeIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isOpen) {
            hadSelectionWhileOpenRef.current = false;
            lastSelectedEdgeIdRef.current = null;
            resetRefs();
            return;
        }

        if (selectedEdge) {
            if (lastSelectedEdgeIdRef.current !== selectedEdge.id) {
                lastSelectedEdgeIdRef.current = selectedEdge.id;
                resetRefs();
            }
            hadSelectionWhileOpenRef.current = true;
            return;
        }

        if (!hadSelectionWhileOpenRef.current && !hasUrlRestorationPending) {
            drawerUtils.hide(DrawerId.Condition, [selectionParam]);
        }
    }, [isOpen, selectedEdge, drawerUtils, selectionParam, hasUrlRestorationPending, resetRefs]);

    const handleDelete = () => {
        deleteSelectedEdge();
        resetRefs();
        drawerUtils.hide(DrawerId.Condition, [selectionParam]);
    };

    // Called on every form change to keep track of current values
    const handleFormChange = useCallback((data: ConditionFormData) => {
        if (initialFormDataRef.current === null) {
            initialFormDataRef.current = serializeForDirtyCheck(data);
        }
        formDataRef.current = data;
    }, []);

    // Save form data to edge and close drawer
    const handleDone = async () => {
        if (validateFormRef.current) {
            const isValid = await validateFormRef.current();
            if (!isValid) return;
        }

        if (formDataRef.current) {
            updateEdgeData(formDataRef.current as unknown as Record<string, unknown>);
        }
        resetRefs();
        drawerUtils.hide(DrawerId.Condition, [selectionParam]);
    };

    // Check if form has unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        if (changesSavedRef.current) {
            return false;
        }

        const currentData = formDataRef.current;

        if (!currentData || initialFormDataRef.current === null) {
            return false;
        }

        // Compare current form data with initial form data (captured on first load)
        return serializeForDirtyCheck(currentData) !== initialFormDataRef.current;
    }, []);

    // Handle close with unsaved changes warning
    const handleBeforeClose = useCallback((): Promise<boolean> => {
        if (changesSavedRef.current) {
            resetRefs();
            return Promise.resolve(true);
        }

        if (!hasUnsavedChanges()) {
            resetRefs();
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            Modal.confirm({
                title: tC("unsaved_changes"),
                content: tC("unsaved_changes_message"),
                okText: tC("discard"),
                cancelText: tC("cancel"),
                onOk: () => {
                    resetRefs();
                    resolve(true);
                },
                onCancel: () => resolve(false),
            });
        });
    }, [hasUnsavedChanges, tC, resetRefs]);

    return (
        <Drawer
            drawerId={DrawerId.Condition}
            title={<DrawerTitle mainTitle={t("edges.condition.drawers.title")} />}
            onBeforeClose={handleBeforeClose}
            searchParamsToClean={[selectionParam]}
            extra={
                <Flex gap={"small"}>
                    <Button size={"large"} type={"primary"} danger onClick={handleDelete}>
                        {tC("delete")}
                    </Button>
                    <Button size={"large"} type={"primary"} onClick={handleDone}>
                        {tC("done")}
                    </Button>
                </Flex>
            }
        >
            <DrawerContent>
                <ConditionForm
                    key={selectedEdge?.id}
                    formId={FormId.ConditionForm}
                    defaultValues={formData}
                    onChange={handleFormChange}
                    onRegisterValidator={(fn: () => Promise<boolean>) => {
                        validateFormRef.current = fn;
                    }}
                    formFields={formFields}
                    customFields={customFields}
                />
            </DrawerContent>
        </Drawer>
    );
};
